import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel
} from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { classNames } from '../../helpers'

const Filter = ({
  id,
  label,
  value = null, // the filter can now also be a dropdown with options
  column,
  handleChange,
  debounce,
  options
}) => {
  if (options && options.length > 0) {
    return (
      <Disclosure as='div' className='py-1'>
        {({ open }) => (
          <>
            <DisclosureButton className='flex w-full items-center justify-between text-left'>
              <div className='flex cursor-pointer items-center'>
                <label className='ml-3 cursor-pointer whitespace-nowrap pr-6 text-sm font-normal text-gray-900'>
                  {label}
                </label>
              </div>
              <ChevronDownIcon
                className={classNames(
                  open ? '-rotate-180' : 'rotate-0',
                  'h-4 w-4 transform text-gray-500'
                )}
                aria-hidden='true'
              />
            </DisclosureButton>
            <DisclosurePanel className='mt-1 pl-7'>
              <div className='space-y-1'>
                {options.map((option, index) => (
                  <Filter
                    key={`${id}-${index}`}
                    id={`${id}-${index}`}
                    label={option.label}
                    value={option.value}
                    column={column}
                    handleChange={handleChange}
                    debounce={debounce}
                  />
                ))}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    )
  }

  const checked = (column.getFilterValue() || []).includes(value)

  const handleFilterChange = (event) => {
    const newChecked = event.target.checked
    const currentValues = column.getFilterValue() || []
    let newValues

    if (newChecked) {
      newValues = [...currentValues, value]
    } else {
      newValues = currentValues.filter((v) => v !== value)
    }

    column.setFilterValue(newValues)
    handleChange({
      column,
      value,
      checked: newChecked
    })
  }

  return (
    <div className='flex cursor-pointer items-center'>
      <input
        id={id}
        type='checkbox'
        checked={checked}
        onChange={handleFilterChange}
        className='h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-600'
      />
      <label
        htmlFor={id}
        className='ml-3 cursor-pointer whitespace-nowrap pr-6 text-sm font-normal text-gray-900'
      >
        {label}
      </label>
    </div>
  )
}

Filter.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  column: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  debounce: PropTypes.number,
  options: PropTypes.array
}

Filter.defaultProps = {
  debounce: 200,
  options: null
}

/**
 * Nicht einklappbare Gruppe im Filter-Popover (Muster Linear/Notion): kleine
 * graue Überschrift, davor eine Trennlinie, Einträge im selben Rhythmus und
 * ohne Einrückung wie die übrigen Optionen. Für kleine, immer sichtbare
 * Gruppen wie die Talent-Status. Große Gruppen (Zusätzliche Daten) bleiben
 * beim einklappbaren Filter.
 */
export const FilterSection = ({
  id,
  label,
  options,
  column,
  handleChange,
  itemClassName,
  withDivider
}) => (
  <div
    className={classNames(withDivider && 'mt-1 border-t border-gray-100 pt-3')}
  >
    <p className='px-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>
      {label}
    </p>
    {options.map((option, index) => (
      <div key={`${id}-${index}`} className={itemClassName}>
        <Filter
          id={`${id}-${index}`}
          label={option.label}
          value={option.value}
          column={column}
          handleChange={handleChange}
        />
      </div>
    ))}
  </div>
)

FilterSection.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  column: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  itemClassName: PropTypes.string,
  withDivider: PropTypes.bool
}

FilterSection.defaultProps = {
  itemClassName: undefined,
  withDivider: false
}

export default Filter
